import { zodResolver } from '@hookform/resolvers/zod'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded'
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { motion } from 'framer-motion'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'

import { getApiErrorMessage } from '@/api/apiError'
import { authApi } from '@/features/auth/api/authApi'
import { resetPasswordSchema, type ResetPasswordFormValues } from '@/features/auth/schemas'
import { paths } from '@/routes/paths'

// PC-FINAL-3: pantalla mínima de confirmación de restablecimiento. El
// campo "código" se pre-llena si llega ?token= en la URL (enlace del
// correo cuando FRONTEND_BASE_URL está configurado en el backend), pero
// sigue siendo editable -- también funciona pegando el código a mano si
// el correo no trae enlace (comportamiento por defecto sin esa variable).
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [formError, setFormError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const tokenFromUrl = useMemo(() => searchParams.get('token') ?? '', [searchParams])

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { token: tokenFromUrl, newPassword: '', confirmPassword: '' },
  })

  const onSubmit = async (values: ResetPasswordFormValues) => {
    setFormError(null)
    try {
      await authApi.confirmPasswordReset(values.token, values.newPassword)
      setSuccess(true)
    } catch (error) {
      setFormError(
        getApiErrorMessage(error, 'No fue posible restablecer la contraseña.'),
      )
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
    >
      <Stack spacing={0.75} sx={{ mb: 4 }}>
        <Typography variant="h3">Restablecer contraseña</Typography>
        <Typography variant="body2" color="text.secondary">
          Ingresa el código que recibiste por correo y tu nueva contraseña.
        </Typography>
      </Stack>

      {success ? (
        <Stack spacing={3}>
          <Alert severity="success">
            Contraseña restablecida correctamente. Ya puedes iniciar sesión.
          </Alert>
          <Button variant="contained" size="large" fullWidth onClick={() => navigate(paths.login)}>
            Ir a iniciar sesión
          </Button>
        </Stack>
      ) : (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2.5}>
            {formError && <Alert severity="error">{formError}</Alert>}

            <Controller
              name="token"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Código de restablecimiento"
                  fullWidth
                  autoFocus={!tokenFromUrl}
                  error={Boolean(errors.token)}
                  helperText={errors.token?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <VpnKeyRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nueva contraseña"
                  type="password"
                  autoComplete="new-password"
                  autoFocus={Boolean(tokenFromUrl)}
                  fullWidth
                  error={Boolean(errors.newPassword)}
                  helperText={errors.newPassword?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Confirmar nueva contraseña"
                  type="password"
                  autoComplete="new-password"
                  fullWidth
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword?.message}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />

            <Button type="submit" variant="contained" size="large" loading={isSubmitting} fullWidth>
              Restablecer contraseña
            </Button>

            <MuiLink component={RouterLink} to={paths.login} variant="body2" sx={{ alignSelf: 'center' }}>
              Volver a iniciar sesión
            </MuiLink>
          </Stack>
        </Box>
      )}
    </motion.div>
  )
}
